import { GraduationCap, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { getCategories, getQuestionsByCategory } from '../services/api';
import Loader from '../components/Loader';

// Utility function to convert subject name to URL-friendly slug
const createSlug = (name: string): string => {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters except spaces and hyphens
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .trim();
};

interface Category {
  _id: string;
  name: string;
  description: string;
}

const FreeQuizPage = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const res = await getCategories();
      setCategories(Array.isArray(res) ? res : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load categories');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <Loader variant="fullscreen" size="xl" text="Loading categories..." />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-lg text-red-600 dark:text-red-400 mb-4">{error}</p>
          <button
            onClick={fetchCategories}
                            className="px-4 py-2 bg-green-800 hover:bg-green-900 text-white rounded-[24px] transition-colors duration-200"
            aria-label="Try Again"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-green-600 to-green-800 text-white py-24">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Free Agri Quiz
            </h1>
            <p className="text-xl text-green-100 max-w-3xl mx-auto mb-8">
              Test your knowledge with our free agricultural quizzes
            </p>
          </div>
        </div>
      </section>

      {/* Subjects Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {categories.map((cat) => (
              <div 
                key={cat._id}
                className="bg-white dark:bg-gray-800 rounded-[24px] overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <div className="h-12 w-12 bg-green-100 dark:bg-green-900/50 rounded-[24px] flex items-center justify-center mr-4">
                      <GraduationCap className="h-6 w-6 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                        {cat.name}
                      </h2>
                      {/* Optionally, you can show question count if you fetch it */}
                    </div>
                  </div>

                  <p className="text-gray-600 dark:text-gray-300 mb-6">
                    {cat.description}
                  </p>

                  <button
                    onClick={async () => {
                      try {
                        // Fetch the first question to get its slug
                        const questions = await getQuestionsByCategory(cat._id);
                        if (Array.isArray(questions) && questions.length > 0) {
                          const firstQuestionSlug = questions[0].question
                            .toLowerCase()
                            .replace(/[^a-z0-9\s-]/g, '')
                            .replace(/\s+/g, '-')
                            .replace(/-+/g, '-')
                            .substring(0, 100)
                            .trim();
                          navigate(`/free-agriculture-quiz/${createSlug(cat.name)}/${firstQuestionSlug}`);
                        } else {
                          // Fallback to category-only URL if no questions
                          navigate(`/free-agriculture-quiz/${createSlug(cat.name)}/${cat._id}`);
                        }
                      } catch (error) {
                        console.error('Error fetching questions:', error);
                        // Fallback to category-only URL
                        navigate(`/free-agriculture-quiz/${createSlug(cat.name)}/${cat._id}`);
                      }
                    }}
                    className="w-full py-3 px-4 bg-green-800 hover:bg-green-900 text-white rounded-[24px] text-sm font-medium transition-colors flex items-center justify-center"
                    aria-label="Start Quiz"
                  >
                    <span>Start Quiz</span>
                    <ChevronRight className="h-4 w-4 ml-2" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {categories.length === 0 && !isLoading && (
            <div className="text-center py-12">
              <p className="text-lg text-gray-600 dark:text-gray-300 mb-4">No categories available at the moment.</p>
              <button
                onClick={fetchCategories}
                className="px-4 py-2 bg-green-800 hover:bg-green-900 text-white rounded-[24px] transition-colors duration-200"
                aria-label="Refresh"
              >
                Refresh
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Why Take Our Free Quizzes?
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300">
              Enhance your agricultural knowledge with our comprehensive quizzes
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white dark:bg-gray-900 rounded-[24px] p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Instant Feedback
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Get immediate results and explanations for each question
              </p>
            </div>
            <div className="bg-white dark:bg-gray-900 rounded-[24px] p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Comprehensive Coverage
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Questions covering all important topics in agricultural sciences
              </p>
            </div>
            <div className="bg-white dark:bg-gray-900 rounded-[24px] p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Track Progress
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Monitor your performance and identify areas for improvement
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FreeQuizPage; 